import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import dayjs from "dayjs";
import { Alert, Button, Modal, Spin, Tag, Tooltip } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { PDFDocumentProxy } from "pdfjs-dist";

import { templatesApi } from "../../../api/presentationsApi";
import {
  ChecklistStatus,
  IPresentationTemplate,
  ISlide,
  ISlideOutline,
  ISlideSection,
  ITemplateRow,
} from "../../../types/models/presentations";
import FloorPlanWizard from "../floorPlans/FloorPlanWizard";
import { MapImageEditor } from "../maps/MapImageModal";
import { GalleryPicksEditor } from "../gallery/GalleryPicksModal";
import { AmenityPhotosEditor } from "../gallery/AmenityPhotosModal";
import { CopyFieldsEditor } from "../copy/CopyFieldsModal";
import DevelopmentSlidePanel from "../developments/DevelopmentSlidePanel";
import LinkDevelopmentModal from "../developments/LinkDevelopmentModal";
import PaymentPlansPanel from "../paymentPlans/PaymentPlansPanel";
import AppreciationEditor from "./AppreciationEditor";
import AddLevelForm from "./AddLevelForm";
import { openPdf } from "./pdfPages";
import SlidePage from "./SlidePage";

/**
 * Prepare one property's brochure, slide by slide.
 *
 * The deck in order down the left, the selected slide large in the middle,
 * and on the right everything that slide asks of the property — its copy, its
 * photos, its floor plan, the map, the payment plans — each editor saving on
 * its own. People think of a brochure as its slides ("the text on slide 7"),
 * so this is laid out the way they look for things, not the way the data is
 * stored.
 *
 * The server's outline (`slides.outline`) says what sits on each slide, what
 * is wrong with it and whether this property loses it. Thumbnails come from
 * the latest brochure when there is one, so a person sees what they are
 * fixing, and from the template otherwise. Generate stays here: the screen
 * waits for the new brochure and redraws from it, on the same slide.
 *
 * Unsaved edits are asked about before moving to another slide or closing.
 */

const ICONS: Record<ChecklistStatus, React.ReactNode> = {
  done: <CheckCircleFilled style={{ color: "#52c41a" }} />,
  blocking: <CloseCircleFilled style={{ color: "#ff4d4f" }} />,
  warning: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
};

const RAIL_THUMB = 176;
const RUN_THUMB = 52;

/** antd v4's Modal, edge to edge: no margin, border, radius or outer scroll. */
const FullScreen = createGlobalStyle`
  .prepare-fullscreen {
    overflow: hidden;
  }
  .prepare-fullscreen .ant-modal {
    top: 0;
    max-width: 100vw;
    margin: 0;
    padding: 0;
  }
  .prepare-fullscreen .ant-modal-content {
    border-radius: 0;
    box-shadow: none;
  }
  .prepare-fullscreen .ant-modal-body {
    height: 100vh;
    padding: 0;
    overflow: hidden;
  }
`;

const Screen = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  .head {
    flex: 0 0 56px;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 0 16px 0 20px;
    border-bottom: 1px solid #f0f0f0;
  }
  .head .title { font-size: 16px; font-weight: 600; }
  .head .template { color: rgba(0, 0, 0, 0.45); }
  .head .summary { margin-left: auto; color: rgba(0, 0, 0, 0.55); }

  .columns {
    flex: 1;
    min-height: 0;
    display: flex;
  }

  .rail {
    flex: 0 0 ${RAIL_THUMB + 40}px;
    position: relative;   /* the items' offsetTop, for scrolling to the selected one */
    overflow-y: auto;
    background: #fafafa;
    border-right: 1px solid #f0f0f0;
    padding: 12px;
  }

  .stage {
    flex: 0 0 38%;
    overflow-y: auto;
    background: #525659;
    padding: 20px;
    color: rgba(255, 255, 255, 0.85);
  }
  .stage .caption { margin-bottom: 10px; }
  .stage .caption .source { color: rgba(255, 255, 255, 0.55); font-size: 12px; }
  .stage .empty {
    aspect-ratio: var(--slide-aspect);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 24px;
    border: 1px dashed rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.6);
  }
  .stage canvas { display: block; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4); }

  .editors {
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    padding: 20px 24px 40px;
  }
  .editors .slide-title { font-size: 18px; font-weight: 600; margin-bottom: 12px; }
  .editors .problems { margin-bottom: 16px; display: grid; gap: 8px; }
  .section { padding: 16px 0 24px; border-top: 1px solid #f0f0f0; }
  .section > h4 {
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 12px;
  }
  .fields { display: grid; grid-template-columns: max-content 1fr; gap: 6px 16px; }
  .fields .label { color: rgba(0, 0, 0, 0.55); }
  .fields .blank { color: #d48806; }
  .pager { display: flex; justify-content: space-between; margin-top: 24px; }
`;

const RailItem = styled.button<{ selected: boolean; quiet?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 2px solid ${({ selected }) => (selected ? "#c49662" : "transparent")};
  border-radius: 2px;
  background: ${({ selected }) => (selected ? "#fff" : "transparent")};
  padding: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  opacity: ${({ quiet }) => (quiet ? 0.6 : 1)};

  .line { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 12px; }
  .number { color: rgba(0, 0, 0, 0.45); font-variant-numeric: tabular-nums; }
  .name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .strip { display: flex; gap: 4px; flex-wrap: wrap; }
  .thumb-empty {
    width: ${RAIL_THUMB}px;
    aspect-ratio: var(--slide-aspect);
    background: #f0f0f0;
  }
`;

/** A slide with nothing to edit, and nothing wrong. Shown folded with its neighbours. */
const isQuiet = (slide: ISlide) =>
  slide.sections.length === 0 && slide.problems.length === 0 && !slide.dropped;

/** The rail: editable slides one by one, runs of quiet ones folded together. */
export const railEntries = (slides: ISlide[]) => {
  const entries: { slides: ISlide[] }[] = [];
  slides.forEach((slide) => {
    const last = entries[entries.length - 1];
    if (isQuiet(slide) && last && isQuiet(last.slides[0])) last.slides.push(slide);
    else entries.push({ slides: [slide] });
  });
  return entries;
};

/** Where to open: the first blocking slide, else the first warning, else the first with inputs. */
export const firstSlide = (slides: ISlide[]) =>
  (slides.find((s) => s.status === "blocking")
    ?? slides.find((s) => s.status === "warning" && !s.hidden)
    ?? slides.find((s) => s.sections.length > 0)
    ?? slides[0])?.number;

/** The PDF behind the thumbnails, reopened only when it is a different file. */
const usePdf = (outline?: ISlideOutline) => {
  const [doc, setDoc] = useState<PDFDocumentProxy>();
  const preview = outline?.preview;
  // A signed URL is new on every response; the file is not.
  const identity = preview && preview.url
    ? `${preview.source}:${preview.presentation}:${preview.generated_at}`
    : null;
  const url = useRef<string | null>(null);
  url.current = preview?.url ?? null;

  useEffect(() => {
    if (!identity || !url.current) { setDoc(undefined); return undefined; }
    let live = true;
    let opened: PDFDocumentProxy | undefined;
    openPdf(url.current)
      .then((result) => { opened = result; if (live) setDoc(result); })
      .catch(() => live && setDoc(undefined));
    return () => { live = false; opened?.destroy(); };
  }, [identity]);

  return doc;
};

/** How wide the middle column draws its page. */
const useWidth = (fallback: number) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(240, Math.floor(entry.contentRect.width)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
};

export interface PrepareModalProps {
  template: IPresentationTemplate;
  /** Kept current by the drawer, so a generation in flight shows here. */
  row: ITemplateRow;
  open: boolean;
  onClose: () => void;
  /** Generate the row. Only offered when nothing is blocking. */
  onGenerate: (row: ITemplateRow) => void;
  /** Open the property's edit wizard; the drawer owns it. */
  onEditListing: (slug: string) => void;
  /** Bumped by the drawer when something it owns (the edit wizard) closes. */
  refreshKey?: number;
  /** Something was saved, so the drawer's own table may be out of date. */
  onChanged?: () => void;
  /** The slide to open on; the first that needs something otherwise. */
  initialSlide?: number;
}

const PrepareModal = ({
  template, row, open, onClose, onGenerate, onEditListing, refreshKey, onChanged,
  initialSlide,
}: PrepareModalProps) => {
  const [outline, setOutline] = useState<ISlideOutline>();
  const [error, setError] = useState<string>();
  const [selected, setSelected] = useState<number | undefined>(initialSlide);
  const [building, setBuilding] = useState(false);
  const templateId = Number(template.id);
  const listingId = row.listing.id;

  const load = useCallback(() => {
    setError(undefined);
    return templatesApi.slides(templateId, listingId)
      .then((result) => {
        setOutline(result);
        setSelected((current) => current ?? firstSlide(result.slides));
        return result;
      })
      .catch((e: any) => { setError(e?.message ?? "Could not load the slides."); return undefined; });
  }, [templateId, listingId]);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  // A template nobody has previewed has no PDF to draw from; build it once.
  useEffect(() => {
    if (!outline || outline.preview.source !== "template" || outline.preview.url || building) return;
    setBuilding(true);
    templatesApi.preview(templateId)
      .then(() => load())
      .catch(() => undefined)
      .finally(() => setBuilding(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outline?.preview.source, outline?.preview.url]);

  // A new brochure: redraw from it, on the same slide.
  // The job is queued before the row says `pending`, so the click counts too.
  const [queued, setQueued] = useState(false);
  const generatedAt = row.presentation?.generated_at ?? null;
  const status = row.presentation?.status;
  const generating = queued || status === "pending";
  const lastGenerated = useRef(generatedAt);
  useEffect(() => {
    if (generatedAt !== lastGenerated.current) {
      lastGenerated.current = generatedAt;
      setQueued(false);
      if (open) load();
    }
  }, [generatedAt, open, load]);
  // However it ended — a new brochure, a failure, a job refused — it ended.
  const wasPending = useRef(false);
  useEffect(() => {
    if (status === "pending") wasPending.current = true;
    else if (wasPending.current || status === "failed" || status === "needs_info") {
      wasPending.current = false;
      setQueued(false);
    }
  }, [status]);

  const doc = usePdf(outline);
  const [stageRef, stageWidth] = useWidth(560);

  /** Unsaved edits, per section; read when leaving, so a ref, not state. */
  const dirty = useRef<Record<string, boolean>>({});
  const dirtyHandlers = useRef<Record<string, (value: boolean) => void>>({});
  const onDirty = (key: string) => {
    if (!dirtyHandlers.current[key]) {
      dirtyHandlers.current[key] = (value: boolean) => { dirty.current[key] = value; };
    }
    return dirtyHandlers.current[key];
  };
  const unsaved = () => Object.values(dirty.current).some(Boolean);

  /** Leave the current slide, asking first if something on it is unsaved. */
  const leave = (then: () => void) => {
    if (!unsaved()) { then(); return; }
    Modal.confirm({
      title: "Leave without saving?",
      content: "Something on this slide has changes that are not saved yet.",
      okText: "Discard changes",
      okButtonProps: { danger: true },
      cancelText: "Stay",
      onOk: () => { dirty.current = {}; then(); },
    });
  };

  const go = (number: number) => {
    if (number === selected) return;
    leave(() => {
      setSelected(number);
      // Statuses can move under edits nothing here tracks (payment plans, the
      // floor-plan wizard closed without publishing), so each move re-checks.
      load();
    });
  };

  // A level just added from its slide: the wizard opens on it by itself.
  const [newLevelId, setNewLevelId] = useState<number>();

  const saved = () => {
    load();
    onChanged?.();
  };

  const slides = useMemo(() => outline?.slides ?? [], [outline]);

  // Keep the selected slide in view in the rail. Opening on slide 12 with the
  // rail still showing slide 1 left people wondering why slide 12 was open.
  // Centred when it is out of view; left alone when it already shows, so
  // clicking one slide never makes the rail jump.
  const rail = useRef<HTMLElement>(null);
  useEffect(() => {
    const container = rail.current;
    const item = container?.querySelector<HTMLElement>('[data-selected="true"]');
    if (!container || !item) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    const visible = top >= container.scrollTop
      && bottom <= container.scrollTop + container.clientHeight;
    if (!visible) {
      container.scrollTop = top - (container.clientHeight - item.offsetHeight) / 2;
    }
  }, [selected, slides.length]);
  const slide = slides.find((s) => s.number === selected);
  const pageOf = (number: number) => outline?.preview.pages[String(number)];

  const editable = slides.filter((s) => !isQuiet(s) && !s.hidden);
  const position = editable.findIndex((s) => s.number === selected);
  const previous = position > 0 ? editable[position - 1] : undefined;
  const next = position >= 0 && position < editable.length - 1 ? editable[position + 1] : undefined;

  const blocking = slides.filter((s) => s.status === "blocking").length
    + (outline?.general.filter((p) => p.status === "blocking").length ?? 0);
  const warnings = slides.filter((s) => s.status === "warning" && !s.hidden).length;
  const blocked = !outline || outline.blocked;

  const sourceNote = outline?.preview.source === "brochure"
    ? `From the brochure generated ${dayjs(outline.preview.generated_at).format("D MMM, HH:mm")}`
    : "From the template, unfilled — generate to see this property's";

  const section = (s: ISlideSection, index: number) => {
    const key = `${selected}:${s.kind}:${index}`;
    switch (s.kind) {
      case "copy":
        return (
          <div className="section" key={key}>
            <h4>Copy</h4>
            <CopyFieldsEditor embedded listingId={listingId} templateId={templateId}
                              roots={s.roots} onSaved={saved} onDirtyChange={onDirty(key)} />
          </div>
        );
      case "development":
        return (
          <div className="section" key={key}>
            <h4>Development</h4>
            {s.development ? (
              <DevelopmentSlidePanel developmentId={s.development} onChanged={saved} />
            ) : (
              <>
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                       message="This property is not linked to a development, so the slide is left out." />
                <LinkDevelopmentModal listingId={listingId} listingName={row.listing.name}
                                      onLinked={saved}>
                  <Button>Link a development</Button>
                </LinkDevelopmentModal>
              </>
            )}
          </div>
        );
      case "floor_plan":
        // `level` is null when the design has no such storey. The wizard, given
        // no level, opens on the first, so a plan made from it would replace
        // level 1's: the level is added here first, then the wizard opens on it.
        if (s.level === null) {
          return (
            <div className="section" key={key}>
              <h4>Floor plan — level {s.index + 1}</h4>
              <p>
                <Tag>No level {s.index + 1}</Tag> This home's design has no level {s.index + 1} yet.
              </p>
              {s.new_level ? (
                <AddLevelForm number={s.index + 1} newLevel={s.new_level}
                              onAdded={levelId => { setNewLevelId(levelId); saved(); }} />
              ) : (
                <p>
                  {row.listing.design
                    ? `Add level ${s.index} first, on its own slide.`
                    : "This property has no unit, so there is no design to add a level to."}
                </p>
              )}
            </div>
          );
        }
        return (
          <div className="section" key={key}>
            <h4>Floor plan — level {s.index + 1}{s.name ? ` · ${s.name}` : ""}</h4>
            <p>
              {s.published
                ? <><Tag color="success">Published</Tag> This level has a floor plan.</>
                : <><Tag color="error">Missing</Tag> No floor plan published for this level yet.</>}
            </p>
            {row.listing.design ? (
              <FloorPlanWizard designId={row.listing.design} unitId={row.listing.unit ?? undefined}
                               levelId={s.level ?? undefined} recordName={row.listing.name}
                               onPublished={saved}
                               {...(s.level === newLevelId
                                 ? { open: true, onOpenChange: (o: boolean) => { if (!o) setNewLevelId(undefined); } }
                                 : {})}>
                <Button type={s.published ? "default" : "primary"}>
                  {s.published ? "Review or replace" : "Make one"}
                </Button>
              </FloorPlanWizard>
            ) : (
              <Tooltip title="This property has no unit, so there is no design to hold a floor plan.">
                <Button disabled>Make one</Button>
              </Tooltip>
            )}
          </div>
        );
      case "gallery":
        return (
          <div className="section" key={key}>
            <h4>Photos</h4>
            <GalleryPicksEditor listingId={listingId} listingName={row.listing.name}
                                listingSlug={row.listing.slug} slots={s.slots}
                                onSaved={saved} onDirtyChange={onDirty(key)} />
          </div>
        );
      case "amenity_photos":
        return (
          <div className="section" key={key}>
            <h4>Amenity photos</h4>
            <AmenityPhotosEditor listingId={listingId} listingName={row.listing.name}
                                 listingSlug={row.listing.slug}
                                 onSaved={saved} onDirtyChange={onDirty(key)} />
          </div>
        );
      case "map":
        return (
          <div className="section" key={key}>
            <h4>Location map</h4>
            <MapImageEditor listingId={listingId} onSaved={saved} onDirtyChange={onDirty(key)} />
          </div>
        );
      case "payment_plans":
        return (
          <div className="section" key={key}>
            <h4>Payment plans</h4>
            {s.unit
              ? <PaymentPlansPanel unitId={s.unit} />
              : <Alert type="info" showIcon message="This property has no unit to hold payment plans." />}
          </div>
        );
      case "appreciation":
        return (
          <div className="section" key={key}>
            <h4>Annual appreciation</h4>
            {s.destination
              ? <AppreciationEditor destination={s.destination} onSaved={saved}
                                    onDirtyChange={onDirty(key)} />
              : <Alert type="info" showIcon
                       message="This property has no destination, so the band is left out." />}
          </div>
        );
      case "listing":
        return (
          <div className="section" key={key}>
            <h4>Property details</h4>
            <div className="fields">
              {s.fields.map((field) => (
                <React.Fragment key={field.path}>
                  <span className="label">{field.label}</span>
                  {field.value
                    ? <span>{field.value}</span>
                    : <span className="blank">Empty — prints blank</span>}
                </React.Fragment>
              ))}
            </div>
            <Button style={{ marginTop: 12 }} onClick={() => onEditListing(row.listing.slug)}>
              Edit property
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={open}
      onCancel={() => leave(onClose)}
      wrapClassName="prepare-fullscreen"
      width="100vw"
      style={{ top: 0, paddingBottom: 0 }}
      closable={false}
      footer={null}
      title={null}
      destroyOnClose
    >
      <FullScreen />
      <Screen style={{ ["--slide-aspect" as any]: String(outline?.aspect ?? 16 / 9) }}>
        <div className="head">
          <span className="title">{row.listing.name}</span>
          <span className="template">{template.name}</span>
          <span className="summary">
            {!outline ? "" : blocking
              ? `${blocking} slide${blocking === 1 ? "" : "s"} need something before generating`
              : warnings
                ? `Ready · ${warnings} to review`
                : "Everything this template needs is here."}
          </span>
          <Tooltip title={blocked && outline ? "Fix the slides marked red first." : ""}>
            <Button
              type="primary"
              loading={generating}
              disabled={blocked || !template.is_active || !row.in_audience}
              onClick={() => { setQueued(true); onGenerate(row); }}
            >
              {generating ? "Generating…" : "Generate"}
            </Button>
          </Tooltip>
          <Button type="text" aria-label="Close" icon={<CloseOutlined />}
                  onClick={() => leave(onClose)} />
        </div>

        <div className="columns">
          <nav className="rail" aria-label="Slides" ref={rail}>
            {outline?.general.map((problem) => (
              <Alert key={problem.detail} type={problem.status === "blocking" ? "error" : "warning"}
                     message={problem.detail} style={{ marginBottom: 8, fontSize: 12 }} />
            ))}
            {!outline && !error && <Spin />}
            {railEntries(slides).map(({ slides: run }) => {
              const first = run[0];
              const isSelected = run.some((s) => s.number === selected);
              if (run.length > 1 || isQuiet(first)) {
                const last = run[run.length - 1];
                return (
                  <RailItem key={first.number} type="button" quiet selected={isSelected}
                            data-selected={isSelected}
                            onClick={() => go(first.number)}>
                    <div className="strip">
                      {run.map((s) => (
                        <SlidePage key={s.number} doc={doc} page={pageOf(s.number)} width={RUN_THUMB} />
                      ))}
                    </div>
                    <div className="line">
                      <span className="number">
                        {run.length > 1 ? `${first.number}–${last.number}` : first.number}
                      </span>
                      <span className="name">Nothing to edit</span>
                    </div>
                  </RailItem>
                );
              }
              return (
                <RailItem key={first.number} type="button" selected={isSelected}
                          data-selected={isSelected}
                          quiet={!!first.dropped || first.hidden}
                          aria-label={`Slide ${first.number} ${first.label} ${first.status}`}
                          onClick={() => go(first.number)}>
                  {pageOf(first.number)
                    ? <SlidePage doc={doc} page={pageOf(first.number)} width={RAIL_THUMB} />
                    : <div className="thumb-empty" />}
                  <div className="line">
                    <span className="number">{first.number}</span>
                    <span className="name">{first.label}</span>
                    {first.status !== "done" && ICONS[first.status]}
                  </div>
                </RailItem>
              );
            })}
          </nav>

          <div className="stage" ref={stageRef}>
            {slide && (
              <>
                <div className="caption">
                  <div>Slide {slide.number} · {slide.label}</div>
                  <div className="source">{building ? "Building the template preview…" : sourceNote}</div>
                </div>
                {pageOf(slide.number) && doc
                  ? <SlidePage doc={doc} page={pageOf(slide.number)} width={stageWidth - 40} />
                  : (
                    <div className="empty">
                      {slide.dropped
                        ? "Not in the current brochure."
                        : building || (outline?.preview.url && !doc)
                          ? <Spin />
                          : "No preview yet."}
                    </div>
                  )}
              </>
            )}
          </div>

          <div className="editors">
            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
            {slide && (
              <div key={slide.number}>
                <div className="slide-title">Slide {slide.number} · {slide.label}</div>
                {(slide.problems.length > 0 || slide.dropped || slide.hidden) && (
                  <div className="problems">
                    {slide.hidden && (
                      <Alert type="info" showIcon
                             message="Hidden in the template, so it never prints." />
                    )}
                    {slide.dropped && (
                      <Alert type="warning" showIcon message={slide.dropped} />
                    )}
                    {slide.problems.map((problem) => (
                      <Alert key={problem.detail} showIcon message={problem.detail}
                             type={problem.status === "blocking" ? "error" : "warning"} />
                    ))}
                  </div>
                )}
                {slide.sections.length === 0 && (
                  <Alert type="info" showIcon
                         message="Nothing on this slide comes from the property." />
                )}
                {slide.sections.map(section)}
                <div className="pager">
                  {previous
                    ? <Button icon={<LeftOutlined />} onClick={() => go(previous.number)}>
                        {previous.number} · {previous.label}
                      </Button>
                    : <span />}
                  {next && (
                    <Button onClick={() => go(next.number)}>
                      {next.number} · {next.label} <RightOutlined />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Screen>
    </Modal>
  );
};

export default PrepareModal;
